import { useEffect, useRef, useState } from "react";
import { Modal } from "@core/components/Elements/Modal/Modal";
import { TooltipWrapper } from "../Tooltip/TooltipWrapper";
import phoneImage from '@assets-shared/images/target-image.webp';

const SCANNING_TIMEOUT_SECONDS = 30;
const IMAGE_CROSSFADE_MS = 500;

type CoachingOverlayProps = {
  onClose?: () => void;
  text?: string;
  type?: "default" | "immersal";
  variant?: "default" | "smallImage" | "keepText" | "smallImageKeepText";
  currentContentIcon?: string;
  className?: string;
};

export function CoachingOverlay({
  onClose = () => {},
  text,
  type = "default",
  variant = "default",
  currentContentIcon,
  className,
}: CoachingOverlayProps) {
  const isImmersalType = type === "immersal";
  const keepTextWhileScanning = variant === "keepText" || variant === "smallImageKeepText";
  const useSmallImageVariant = variant === "smallImage" || variant === "smallImageKeepText";
  const initText = text || "Point your camera at this artwork";
  const scanningText = "Scanning...";
  const activeImage = currentContentIcon ?? phoneImage;

  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [localizingText, setLocalizingText] = useState(initText);
  const [scanSecondsRemaining, setScanSecondsRemaining] = useState(SCANNING_TIMEOUT_SECONDS);

  // Image rendering strategy:
  //   - bottomImage is the last "settled" image. It is NEVER swapped while
  //     a cross-fade is starting — only after the top's fade-in completes
  //     (or when an in-flight fade is interrupted by a tap, in which case
  //     the visible in-flight image is "absorbed" into bottom so the user
  //     never reverts back to an older image).
  //   - topImage is rendered ONLY after its bitmap has been decoded, so
  //     the fade-in animation can never play against a not-yet-painted
  //     image and the user never sees an empty / flashing frame.
  const [bottomImage, setBottomImage] = useState<string>(activeImage);
  const [topImage, setTopImage] = useState<string | null>(null);
  const [topReady, setTopReady] = useState<boolean>(false);
  const topImageRef = useRef<string | null>(null);
  const topReadyRef = useRef<boolean>(false);
  topImageRef.current = topImage;
  topReadyRef.current = topReady;

  useEffect(() => {
    if (!activeImage || activeImage === bottomImage) return;
    if (activeImage === topImageRef.current) return;
    // If a previous fade is still visible when the user taps again,
    // snap the in-flight image down to the bottom layer so the older
    // bottom doesn't briefly reappear during the swap.
    if (topReadyRef.current && topImageRef.current) {
      setBottomImage(topImageRef.current);
    }
    setTopImage(activeImage);
    setTopReady(false);
    let cancelled = false;
    const reveal = () => {
      if (cancelled) return;
      setTopReady(true);
    };
    const img = new Image();
    img.onload = reveal;
    img.onerror = reveal;
    img.src = activeImage;
    if (img.complete) {
      reveal();
    } else if (img.decode) {
      img.decode().then(reveal, reveal);
    }
    return () => {
      cancelled = true;
    };
  }, [activeImage, bottomImage]);

  const handleTopFadeComplete = () => {
    if (!topImage) return;
    // Update bottom to match the now-completed top, but leave the top img
    // mounted at opacity 1 (animation `forwards`). It covers the bottom
    // while the bottom's <img> swaps to the new src, so the browser's
    // brief "still-painting previous src" moment is invisible to the user.
    // The top will be replaced (key changes) on the next tap.
    setBottomImage(topImage);
  };


  useEffect(() => {
    if (animationPlayed) return;
    const timeout = window.setTimeout(() => {
      setScanning(true);
      if (!isImmersalType && !keepTextWhileScanning) {
        setLocalizingText(scanningText);
      }
      setAnimationPlayed(true);
    }, 2500);
    return () => window.clearTimeout(timeout);
  }, [animationPlayed, scanningText, isImmersalType, keepTextWhileScanning]);

  useEffect(() => {
    if (!animationPlayed) {
      setLocalizingText(initText);
    }
  }, [initText, animationPlayed]);

  useEffect(() => {
    if (text) {
      setLocalizingText(text);
    }
  }, [text]);

  useEffect(() => {
    setScanning(false);
    setAnimationPlayed(false);
    setLocalizingText(initText);
    setScanSecondsRemaining(SCANNING_TIMEOUT_SECONDS);
  }, [activeImage, initText]);

  useEffect(() => {
    if (!isImmersalType) {
      setScanSecondsRemaining(SCANNING_TIMEOUT_SECONDS);
      return;
    }

    setScanSecondsRemaining(SCANNING_TIMEOUT_SECONDS);
    const intervalId = window.setInterval(() => {
      setScanSecondsRemaining((previous) => {
        if (previous <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isImmersalType]);

  const scanProgress = scanSecondsRemaining / SCANNING_TIMEOUT_SECONDS;
  const circleRadius = 7;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const circleDashOffset = circleCircumference * (1 - scanProgress);
  const shouldSmallImage = useSmallImageVariant;

  const imageLayers = (
    <>
      <img
        src={bottomImage}
        alt=""
        aria-hidden="true"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {topReady && topImage && (
        <img
          key={topImage}
          src={topImage}
          alt=""
          aria-hidden="true"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover animate-fade-in"
          style={{
            animationDuration: `${IMAGE_CROSSFADE_MS}ms`,
            animationTimingFunction: "ease-in-out",
            // `both` applies frame-0 styles BEFORE the animation runs, so
            // there is never a 1-frame paint at default opacity:1 before
            // the fade-in starts at 0.
            animationFillMode: "both",
          }}
          onAnimationEnd={handleTopFadeComplete}
        />
      )}
    </>
  );

  return (
      <Modal className={`max-w-[298px] max-h-[388px] overflow-visible z-[130] ${className ?? ""}`}>
        <div
          className={`
            absolute left-1/2 -bottom-0 -translate-x-1/2 origin-bottom w-full max-w-[298px]
            transition-all duration-1000 ease-out
          `}
          style={{ width: shouldSmallImage ? "60%" : "100%", height: shouldSmallImage ? "60%" : "100%" }}
        >
          <div
            className="rounded-xl overflow-hidden w-full h-full max-w-[330px] mx-auto border border-white shadow-[0_3px_40px_rgba(0,0,0,0.3)] relative"
          >
            {imageLayers}
          </div>
        </div>
        <TooltipWrapper
          className="top-full left-1/2 -translate-x-1/2 mt-7 w-[min(90vw,700px)] max-w-[700px] flex justify-center text-lg sm:text-xl"
        >
          <span>{localizingText}</span>
        </TooltipWrapper>
        {isImmersalType && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-[120px] z-[70]">
            <div className="flex items-center gap-[13px] px-5 py-2.5 bg-black/75 backdrop-blur-[6px] rounded-lg border border-white/20">
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                className="-rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx="8.5"
                  cy="8.5"
                  r={circleRadius}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.2)"
                  strokeWidth="3"
                />
                <circle
                  cx="8.5"
                  cy="8.5"
                  r={circleRadius}
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeDasharray={circleCircumference}
                  strokeDashoffset={circleDashOffset}
                  strokeLinecap="round"
                  className="transition-[stroke-dashoffset] duration-1000 ease-linear"
                />
              </svg>
              <span className="text-white text-xl font-medium">Scanning</span>
              <div className="w-px h-8 bg-white/20" />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close scanning"
                className="flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </Modal>
  );
}
